/**
 * Spec 103 — "Cluster candidates" button + modal on /admin/rules/candidates.
 *
 * Mirrors spec 101's RescanCandidatesButton 1:1 for role gating (returns null
 * for non-admins so the control is ABSENT from the DOM, not just disabled),
 * modal markup (`<div role="dialog">` + `bg-black/40` backdrop), useToast use,
 * and error coercion.
 *
 * On confirm: POSTs the cluster endpoint with the operator-selected distance
 * threshold + min cluster size, shows a summary toast, and calls
 * `onClusterComplete()` so the parent can refetch.
 *
 * data-testid attributes wire the spec-103 gates:
 *   cluster-button, cluster-modal, cluster-threshold, cluster-min-size,
 *   cluster-confirm, cluster-cancel
 */
import { useState } from 'react';
import { Icon } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { clusterCandidates } from '../api/ruleIngestion.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';

export interface ClusterCandidatesButtonProps {
  /** Called after a successful cluster run so the parent can refetch. */
  onClusterComplete: () => void;
}

const DEFAULT_DISTANCE_THRESHOLD = 0.3; // cosine distance (~ similarity 0.70)
const DEFAULT_MIN_CLUSTER_SIZE = 2;

export function ClusterCandidatesButton({ onClusterComplete }: ClusterCandidatesButtonProps) {
  const { hasRole } = useAuth();
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [distanceThreshold, setDistanceThreshold] = useState<number>(DEFAULT_DISTANCE_THRESHOLD);
  const [minClusterSize, setMinClusterSize] = useState<number>(DEFAULT_MIN_CLUSTER_SIZE);

  // DOM-level role gate — control absent for non-admins (gate 103.A.6).
  const canCluster = hasRole('rule_admin') || hasRole('orbit:admin');
  if (!canCluster) return null;

  function openModal() {
    if (submitting) return;
    setModalOpen(true);
  }

  function cancelModal() {
    if (submitting) return;
    setModalOpen(false);
  }

  async function runCluster() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await clusterCandidates({
        distance_threshold: distanceThreshold,
        min_cluster_size: minClusterSize,
      });
      toast.addToast(
        `Considered ${result.candidates_considered} · Clusters ${result.clusters_formed} · Proposals ${result.proposals_created} · Singletons ${result.singletons_left}`,
        'success',
      );
      setModalOpen(false);
      onClusterComplete();
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const message = raw.startsWith('Cluster failed:') ? raw : `Cluster failed: ${raw}`;
      toast.addToast(message, 'error');
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button variant="primary" data-testid="cluster-button" onClick={openModal} disabled={submitting}>
        <Icon name="bx-git-merge" className={submitting ? 'animate-spin' : undefined} />
        {submitting ? (
          <span data-testid="cluster-spinner-label">Clustering…</span>
        ) : (
          'Cluster candidates'
        )}
      </Button>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cluster-modal-title"
          data-testid="cluster-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <h2
                id="cluster-modal-title"
                data-testid="cluster-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Cluster candidates into proposed rules
              </h2>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                This groups pending candidates by embedding cosine distance. Each group of at
                least the minimum size becomes a synthesized rule proposal for review.
                Singletons stay pending.
              </p>

              <label className="block text-sm">
                <span className="font-medium text-slate-700">Distance threshold</span>
                <input
                  type="number"
                  min="0.05"
                  max="0.95"
                  step="0.05"
                  value={distanceThreshold}
                  onChange={(e) => setDistanceThreshold(Number(e.target.value))}
                  disabled={submitting}
                  data-testid="cluster-threshold"
                  className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-50"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Cosine distance. Lower = tighter clusters (more, smaller). Default 0.30 ≈
                  similarity 0.70.
                </span>
              </label>

              <label className="block text-sm">
                <span className="font-medium text-slate-700">Min cluster size</span>
                <input
                  type="number"
                  min="2"
                  max="50"
                  step="1"
                  value={minClusterSize}
                  onChange={(e) => setMinClusterSize(Number(e.target.value))}
                  disabled={submitting}
                  data-testid="cluster-min-size"
                  className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-50"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Minimum members for a group to become a proposal. Default 2.
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="ghost" data-testid="cluster-cancel" onClick={cancelModal} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" data-testid="cluster-confirm" onClick={runCluster} disabled={submitting}>
                {submitting ? 'Clustering…' : 'Run clustering'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
